/**
 * Auth context — manages Supabase session lifecycle.
 *
 * Restores sessions and resolves staff authority from the current server profile.
 * A cached JWT role is never a fallback when that lookup fails.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  Fragment,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import type { Role } from '@pazportal/types';
import { useQueryClient } from '@tanstack/react-query';
import { observeAuthSession } from '@pazportal/supabase';
import { supabase } from '../lib/supabase';
import { staffRoleFromProfile } from '../lib/auth';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: Role | null;
  customerId: string | null;
  tripScope: string[] | null;
  signOut: () => Promise<boolean>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [signOutError, setSignOutError] = useState(false);
  const [profile, setProfile] = useState<{
    session: Session;
    role: Role | null;
    customerId: string | null;
  } | null>(null);
  const lastRole = useRef<Role | null>(null);

  useEffect(() => observeAuthSession(supabase.auth, {
    onIdentityChange: () => {
      queryClient.clear();
      setSignOutError(false);
      lastRole.current = null;
      setProfile(null);
    },
    onSession: (nextSession) => {
      setSession(nextSession);
      setLoading(false);
    },
  }), [queryClient]);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    let request = 0;
    let abort: AbortController | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const refreshProfile = (background = false) => {
      const current = ++request;
      abort?.abort();
      clearTimeout(timer);
      const controller = new AbortController();
      abort = controller;
      // A healthy background check must not unmount the page or erase drafts.
      // A rejected lookup still resolves to no role and clears cached data.
      if (!background) setProfile(null);

      const resolve = (role: Role | null, customerId: string | null = null) => {
        if (cancelled || current !== request) return;
        clearTimeout(timer);
        if (lastRole.current !== role) queryClient.clear();
        lastRole.current = role;
        setProfile({ session, role, customerId });
      };

      timer = setTimeout(() => {
        resolve(null);
        request += 1;
        controller.abort();
      }, 10_000);

      void (async () => {
        try {
          const { data: { user }, error } = await supabase.auth.getUser(session.access_token);
          if (cancelled || current !== request) return;
          if (error || !user || user.id !== session.user.id) return resolve(null);
          const { data, error: profileError } = await supabase
            .from('users')
            .select('role, customer_id')
            .eq('auth_user_id', user.id)
            .abortSignal(controller.signal)
            .maybeSingle();
          resolve(profileError ? null : staffRoleFromProfile(data), data?.customer_id ?? null);
        } catch {
          resolve(null);
        }
      })();
    };

    refreshProfile();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshProfile();
    };
    const onFocus = () => refreshProfile();
    const onAuthorityChanged = () => refreshProfile(true);
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refreshProfile(true);
    }, 30_000);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pazportal:network-authority-changed', onAuthorityChanged);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      abort?.abort();
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pazportal:network-authority-changed', onAuthorityChanged);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [session, queryClient]);

  const signOut = useCallback(async () => {
    setSignOutError(false);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch {
      // Supabase retains the persisted session on network/server failure.
      // Keep the UI signed in too, so a reload cannot undo an apparent logout.
      setSignOutError(true);
      return false;
    }
    queryClient.clear();
    setProfile(null);
    setSession(null);
    return true;
  }, [queryClient]);

  const user = session?.user ?? null;
  const resolvedProfile = profile?.session === session ? profile : null;
  const role = resolvedProfile?.role ?? null;
  const customerId = resolvedProfile?.customerId ?? null;
  const tripScope = Array.isArray(user?.app_metadata?.trip_scope) ? user.app_metadata.trip_scope as string[] : null;

  return (
    <AuthContext.Provider
      value={{ user, session, loading: loading || (!!session && !resolvedProfile), role, customerId, tripScope, signOut }}
    >
      <Fragment key={user?.id ?? 'signed-out'}>
        {signOutError && (
          <div role="alert" className="fixed inset-x-4 top-4 z-50 mx-auto max-w-lg rounded-md border border-notification bg-surface p-4 text-sm text-text shadow-lg">
            <p>Sign out could not be completed. You are still signed in. Check your connection and try again.</p>
            <button type="button" onClick={() => void signOut()} className="mt-2 min-h-11 font-medium underline">
              Try signing out again
            </button>
          </div>
        )}
        {children}
      </Fragment>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
